import React, { FC } from 'react'
import { styled, breakpoint, css, breakpointUp } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { Box } from '@moonpig/launchpad-components'
import { NAV_BP, TRANSITON_TIME_MS } from '../constants'
import { useLocaleText } from '../locale'

const POSITION_TOP = 0
const POSITION_MIDDLE = 1
const POSITION_BOTTOM = 2

const StyledBurgerLine = styled.span<{
  isOpen: boolean
  position: number
}>`
  ${({ isOpen, position }) => {
    if (!isOpen) return {}
    const rotation = '45deg'
    const top = '7px'
    switch (position) {
      case POSITION_TOP:
        return css`
          top: ${top};
          transform: rotate(${rotation});
        `
      case POSITION_MIDDLE:
        return css`
          opacity: 0;
          transform: rotate(${rotation});
        `
      case POSITION_BOTTOM:
        return css`
          top: -${top};
          transform: rotate(-${rotation});
        `
      /* istanbul ignore next */
      default:
        return null
    }
  }}
`

const StyledToggle = styled.button<{
  isOpen: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 40px;
  text-indent: -999px;
  line-height: 0;
  justify-content: end;

  ${s({
    mx: { xs: 2, md: 0 },
    my: { xs: -2, md: 0 },
    justifyContent: { xs: 'center', md: 'end' },
    height: { xs: '44px', md: '50px' },
    width: { xs: '44px', md: 'auto' },
  })}

  ${breakpointUp('md')} {
    flex-direction: row;
    position: absolute;
    top: 8px;
    left: 4px;
    height: 44px;
    ${s({ border: 2, borderRadius: 2 })}
    transform: none;

    &:focus {
      outline: 2px solid
        ${({ theme }) => theme.colors.colorInteractionFocusIndicator};
      outline-offset: 4px;
    }

    ${({ isOpen }) =>
      s({
        px: 4,
        ml: 6,
        borderColor: isOpen
          ? 'colorInteractionSelectedState'
          : 'colorInteractionIcon',
      })}
  }

  .line {
    ${({ isOpen }) =>
      s({
        bgcolor: isOpen
          ? 'colorInteractionSelectedState'
          : 'colorInteractionIcon',
        width: { xs: '22px', md: '18px' },
      })}

    position: relative;

    display: block;
    border-radius: 2px;
    transition: transform ${TRANSITON_TIME_MS}, opacity ${TRANSITON_TIME_MS};
    user-select: none;
    height: 2px;
    margin: 5px auto 5px;
  }
`

const StyledToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  font-size: 0;

  ${breakpoint(NAV_BP)} {
    display: none;
  }
`

const StyledLabel = styled.span<{
  $isOpen: boolean
}>`
  ${s({
    display: { xs: 'none', md: 'inline' },
  })}

  text-indent: 0;

  ${({ $isOpen }) =>
    s({
      typography: 'typeBodyCaption',
      color: $isOpen ? 'colorInteractionSelectedState' : 'colorInteractionIcon',
    })}

  ${breakpointUp('md')} {
    ${s({
      typography: 'typeBodyLabel',
    })}
  }
`

export const Toggle: FC<
  React.PropsWithChildren<{
    isOpen: boolean
    as?:
      | keyof React.JSX.IntrinsicElements
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      | React.ComponentType<React.PropsWithChildren<any>>
    onClick: () => void
  }>
> = ({ isOpen, onClick, as }) => {
  const t = useLocaleText()

  return (
    <StyledToggleWrapper>
      <StyledToggle
        onClick={onClick}
        aria-haspopup
        aria-expanded={isOpen}
        aria-controls="mega-nav-content"
        as={as}
        isOpen={isOpen}
        data-testid="lp-nav-meganav-toggle"
        aria-label={t('mega_nav.toggle.title')}
      >
        <Box marginRight={{ xs: 0, md: 4 }}>
          {`${isOpen ? t('mega_nav.toggle.close') : t('mega_nav.toggle.open')}`}
          {[POSITION_TOP, POSITION_MIDDLE, POSITION_BOTTOM].map(position => (
            <StyledBurgerLine
              key={position}
              className="line"
              position={position}
              isOpen={isOpen}
            />
          ))}
        </Box>
        <StyledLabel $isOpen={isOpen} aria-hidden>
          {t('mega_nav.toggle.title')}
        </StyledLabel>
      </StyledToggle>
    </StyledToggleWrapper>
  )
}
